import React, { useEffect, useState } from "react";
import axios from "axios";
import { Target, RefreshCw, CheckCircle2, AlertTriangle, Link2,
         Flame, Send, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const err = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || "").filter(Boolean).join("; ");
  return e?.message || "Ошибка";
};

const card = { background: "white", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16 };
const label = { fontFamily: "Manrope", fontSize: 11, fontWeight: 700, color: "#6b7280",
                letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 6 };
const input = { width: "100%", border: "1px solid #e5e7eb", borderRadius: 6, padding: "9px 11px",
                fontFamily: "Manrope", fontSize: 13, outline: "none", boxSizing: "border-box" };
const btnGhost = { background: "white", color: "#0A0A0A", border: "1px solid #d1d5db",
                   borderRadius: 6, padding: "9px 13px", cursor: "pointer",
                   fontFamily: "Manrope", fontWeight: 600, fontSize: 12.5,
                   display: "flex", alignItems: "center", gap: 6 };

const SOURCE_RU = { facebook_lead: "Facebook · лид-форма",
                    instagram_lead: "Instagram · лид-форма",
                    ctwa_ad: "Реклама → WhatsApp" };
const READY_RU = { hot: "Горячий", warm: "Тёплый", cold: "Холодный" };
const READY_COLOR = { hot: "#dc2626", warm: "#d97706", cold: "#64748b" };

const Flag = ({ ok, label: l, hint }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "7px 0" }}>
    {ok ? <CheckCircle2 size={15} color="#059669" style={{ flexShrink: 0, marginTop: 1 }} />
        : <AlertTriangle size={15} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />}
    <div>
      <div style={{ fontFamily: "Manrope", fontSize: 13, fontWeight: 600 }}>{l}</div>
      {hint && <div style={{ fontFamily: "Manrope", fontSize: 11.5, color: "#8a8a89",
                              lineHeight: 1.45 }}>{hint}</div>}
    </div>
  </div>
);

export default function AdminMetaAds() {
  const [st, setSt] = useState(null);
  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);
  const [testCode, setTestCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [open, setOpen] = useState(null);
  const [sim, setSim] = useState({ phone: "", name: "", when: "", interest: "" });

  const simulate = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const r = await axios.post(`${API}/admin/meta-ads/simulate-lead`, {
        phone: sim.phone, name: sim.name, ad_name: "Тестовое объявление",
        answers: [
          { name: "Телефон", value: sim.phone },
          { name: "Полное имя", value: sim.name },
          { name: "Когда готовы приехать на примерку?", value: sim.when },
          { name: "Что интересует?", value: sim.interest },
        ],
      }, { withCredentials: true });
      setNotice(`Тестовая заявка создана в Mini CRM${r.data.score != null ? `, ИИ-скор ${r.data.score}/100` : ""}`);
      setSim({ phone: "", name: "", when: "", interest: "" });
      await load();
    } catch (e) { setError(err(e)); } finally { setBusy(false); }
  };

  const load = async () => {
    try {
      const [s, l, e] = await Promise.all([
        axios.get(`${API}/admin/meta-ads/status`, { withCredentials: true }),
        axios.get(`${API}/admin/meta-ads/leads`, { withCredentials: true }),
        axios.get(`${API}/admin/meta-ads/events`, { withCredentials: true }),
      ]);
      setSt(s.data); setLeads(l.data.items || []); setEvents(e.data.items || []);
    } catch (e2) { setError(err(e2)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const subscribe = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      await axios.post(`${API}/admin/meta-ads/subscribe`, {}, { withCredentials: true });
      setNotice("Страница подписана на лид-формы — заявки начнут приходить в CRM");
      await load();
    } catch (e) { setError(err(e)); } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const r = await axios.post(`${API}/admin/meta-ads/test-event`,
        { test_event_code: testCode, event_name: "Schedule" }, { withCredentials: true });
      setNotice(`Событие отправлено по клиенту «${r.data.client_name}» — проверьте Events Manager → Test Events`);
      await load();
    } catch (e) { setError(err(e)); } finally { setBusy(false); }
  };

  const retry = async (id) => {
    setBusy(true); setError(""); setNotice("");
    try {
      await axios.post(`${API}/admin/meta-ads/leads/${id}/retry`, {}, { withCredentials: true });
      setNotice("Заявка загружена заново"); await load();
    } catch (e) { setError(err(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="meta-ads-page" style={{ padding: "4px 2px 40px" }}>
      <h2 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 26, margin: "0 0 4px" }}>
        Реклама Meta → CRM
      </h2>
      <div style={{ fontFamily: "Manrope", fontSize: 12.5, color: "#8a8a89",
                     marginBottom: 16, lineHeight: 1.5, maxWidth: 760 }}>
        Заявки из лид-форм Facebook и Instagram падают в общий Mini CRM с тегом источника,
        ИИ сразу оценивает готовность приехать на примерку (0–100), а статусы «Записан на
        примерку» и «Купил» уходят обратно в Facebook — алгоритм начинает искать похожих
        покупателей, а не просто самые дешёвые заявки.
      </div>

      {error && (
        <div data-testid="meta-ads-error" style={{ background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: 8, padding: "10px 12px", color: "#991b1b", fontFamily: "Manrope",
              fontSize: 12.5, marginBottom: 14 }}>{error}</div>
      )}
      {notice && (
        <div data-testid="meta-ads-notice" style={{ background: "#ecfdf5", border: "1px solid #a7f3d0",
              borderRadius: 8, padding: "10px 12px", color: "#065f46", fontFamily: "Manrope",
              fontSize: 12.5, marginBottom: 14 }}>{notice}</div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
                     gap: 14 }}>
        {/* Подключение */}
        <div style={card}>
          <div style={label}><Link2 size={12} style={{ verticalAlign: "-2px" }} /> Подключение</div>
          <Flag ok={!!st?.page_configured} label="Токен Страницы Facebook"
                hint={st?.page_configured ? `Страница ${st.page_id}`
                  : "Нужны META_PAGE_ID и META_PAGE_ACCESS_TOKEN с разрешением leads_retrieval"} />
          <Flag ok={st?.leadgen_subscribed === true} label="Подписка на лид-формы"
                hint={st?.leadgen_subscribed === true ? "Страница подписана на поле leadgen"
                  : (st?.subscribe_error || "Нажмите «Подписать Страницу» — без этого вебхуки не приходят")} />
          <Flag ok={!!st?.capi_configured} label="Conversions API (обратная связь)"
                hint={st?.capi_configured ? `Dataset ${st.dataset_id}`
                  : "Нужны META_DATASET_ID и META_CAPI_ACCESS_TOKEN из Events Manager"} />
          <div style={{ fontFamily: "Manrope", fontSize: 11.5, color: "#8a8a89",
                         marginTop: 8, lineHeight: 1.5, wordBreak: "break-all" }}>
            Webhook URL: {st?.webhook_url || "—"}<br />
            Verify token: {st?.verify_token || "—"}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <button data-testid="meta-ads-subscribe-btn" onClick={subscribe} disabled={busy}
              style={{ ...btnGhost, opacity: busy ? 0.5 : 1 }}>
              <Link2 size={13} /> Подписать Страницу
            </button>
            <button data-testid="meta-ads-refresh-btn" onClick={load} style={btnGhost}>
              <RefreshCw size={13} /> Обновить
            </button>
          </div>
        </div>

        {/* Счётчики + тест */}
        <div style={card}>
          <div style={label}><Target size={12} style={{ verticalAlign: "-2px" }} /> Как идёт</div>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 12 }}>
            {[["Заявок всего", st?.leads_total ?? "—"],
              ["За 24 часа", st?.leads_24h ?? "—"],
              ["Событий в Facebook", st?.events_total ?? "—"],
              ["Ошибок", st?.events_errors ?? "—"]].map(([l, v]) => (
              <div key={l}>
                <div style={{ ...label, marginBottom: 2 }}>{l}</div>
                <div style={{ fontFamily: "Manrope", fontSize: 20, fontWeight: 800 }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ ...label, marginBottom: 4 }}>Тестовое событие (код из Events Manager)</div>
          <input data-testid="meta-ads-test-code" value={testCode}
            onChange={e => setTestCode(e.target.value)} placeholder="TEST12345" style={input} />
          <button data-testid="meta-ads-test-btn" onClick={sendTest} disabled={busy}
            style={{ ...btnGhost, marginTop: 10, opacity: busy ? 0.5 : 1 }}>
            <Send size={13} /> Отправить событие «Запись на примерку»
          </button>
        </div>
      </div>

      {/* Заявки из форм */}
      <div style={{ ...card, marginTop: 16 }}>
        <div style={label}><Sparkles size={12} style={{ verticalAlign: "-2px" }} /> Заявки из лид-форм</div>
        {leads.length === 0 && (
          <div style={{ fontFamily: "Manrope", fontSize: 12.5, color: "#9ca3af" }}>
            Заявок ещё не было. После подписки Страницы отправьте тестовую заявку из
            предпросмотра формы в Ads Manager — она появится здесь за пару секунд.
          </div>
        )}
        {leads.map(l => (
          <div key={l.id} data-testid={`meta-lead-${l.id}`}
            style={{ borderBottom: "1px solid #f8fafc", padding: "10px 0" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              {typeof l.score === "number" && (
                <div style={{ display: "flex", alignItems: "center", gap: 4,
                               background: "#FDFBF7", border: "1px solid #f1f5f9",
                               borderRadius: 999, padding: "3px 9px",
                               color: READY_COLOR[l.readiness] || "#0A0A0A",
                               fontFamily: "Manrope", fontSize: 12, fontWeight: 800 }}>
                  <Flame size={12} /> {l.score}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 190 }}>
                <div style={{ fontFamily: "Manrope", fontSize: 13, fontWeight: 600 }}>
                  {l.name || "Без имени"} · {l.phone || "нет телефона"}
                </div>
                <div style={{ fontFamily: "Manrope", fontSize: 11.5, color: "#9ca3af" }}>
                  {SOURCE_RU[l.source] || l.source} · {l.ad_name || l.form_name || "—"} ·{" "}
                  {new Date(l.created_at).toLocaleString("ru-RU")}
                </div>
              </div>
              {l.readiness && (
                <div style={{ fontFamily: "Manrope", fontSize: 11.5, fontWeight: 700,
                               color: READY_COLOR[l.readiness] }}>
                  {READY_RU[l.readiness]}
                </div>
              )}
              <button onClick={() => setOpen(open === l.id ? null : l.id)} style={btnGhost}>
                Ответы формы
              </button>
              {(l.error || !l.client_id) && (
                <button data-testid={`meta-lead-retry-${l.id}`} onClick={() => retry(l.id)}
                  style={{ ...btnGhost, color: "#b91c1c", borderColor: "#fca5a5" }}>
                  <RefreshCw size={13} /> Повторить
                </button>
              )}
            </div>
            {l.error && (
              <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#b91c1c", marginTop: 5 }}>
                {l.error}
              </div>
            )}
            {open === l.id && (
              <div style={{ marginTop: 8, background: "#FDFBF7", borderRadius: 8, padding: 10 }}>
                {(l.answers || []).length === 0 && (
                  <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#9ca3af" }}>
                    Ответы не получены
                  </div>
                )}
                {(l.answers || []).map((a, i) => (
                  <div key={i} style={{ fontFamily: "Manrope", fontSize: 12.5, padding: "2px 0" }}>
                    <span style={{ color: "#8a8a89" }}>{a.name}:</span> <strong>{a.value}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Проверка воронки без рекламы */}
      <div style={{ ...card, marginTop: 16 }}>
        <div style={label}><Flame size={12} style={{ verticalAlign: "-2px" }} /> Проверить воронку</div>
        <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#8a8a89",
                       marginBottom: 10, lineHeight: 1.5 }}>
          Создаёт тестовую заявку как из лид-формы: карточка в Mini CRM, ИИ-скоринг
          готовности, задача менеджеру. Так можно проверить всё до запуска рекламы.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
                       gap: 10 }}>
          <input data-testid="meta-sim-phone" value={sim.phone}
            onChange={e => setSim({ ...sim, phone: e.target.value })}
            placeholder="Телефон, +7 701 000 00 00" style={input} />
          <input data-testid="meta-sim-name" value={sim.name}
            onChange={e => setSim({ ...sim, name: e.target.value })}
            placeholder="Имя клиента" style={input} />
          <input data-testid="meta-sim-when" value={sim.when}
            onChange={e => setSim({ ...sim, when: e.target.value })}
            placeholder="Когда готов приехать" style={input} />
          <input data-testid="meta-sim-interest" value={sim.interest}
            onChange={e => setSim({ ...sim, interest: e.target.value })}
            placeholder="Что интересует" style={input} />
        </div>
        <button data-testid="meta-sim-btn" onClick={simulate} disabled={busy || !sim.phone}
          style={{ ...btnGhost, marginTop: 10, opacity: (busy || !sim.phone) ? 0.5 : 1 }}>
          <Flame size={13} /> Создать тестовую заявку
        </button>
      </div>

      {/* Журнал событий в Facebook */}
      <div style={{ ...card, marginTop: 16 }}>
        <div style={label}>События, отправленные в Facebook</div>
        {events.length === 0 && (
          <div style={{ fontFamily: "Manrope", fontSize: 12.5, color: "#9ca3af" }}>
            Пока ничего не отправляли. События уходят автоматически при статусах
            «Записан на примерку» и «Купил» у лидов из рекламы.
          </div>
        )}
        {events.map(e => (
          <div key={e.event_id} data-testid={`capi-event-${e.event_id}`}
            style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
                      padding: "8px 0", borderBottom: "1px solid #f8fafc" }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontFamily: "Manrope", fontSize: 13, fontWeight: 600 }}>
                {e.event_name} · {e.client_name || e.phone || "—"}
                {e.test ? " · тест" : ""}
              </div>
              <div style={{ fontFamily: "Manrope", fontSize: 11.5, color: "#9ca3af" }}>
                {e.attribution === "ctwa" ? "Click-to-WhatsApp" : "Лид-форма"} ·{" "}
                {new Date(e.updated_at).toLocaleString("ru-RU")}
              </div>
            </div>
            <div style={{ fontFamily: "Manrope", fontSize: 11.5, fontWeight: 700,
                           color: e.status === "sent" ? "#047857" : "#b91c1c" }}>
              {e.status === "sent" ? "Отправлено" : (e.error || "Ошибка")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
